// client/src/features/compliance-calendar/search.ts — the calendar's URL state · MAINT-009
// (04 §9.2.3).
//
// §9.2.3 is NORMATIVE: „filters, pagination and sort live in the URL as Zod-validated search
// params". A calendar's filters are which RENDERING is showing, which MONTH it is showing, how
// far the agenda looks and which lane and property are in view — so all five are params, and
// „calendarul lui octombrie, doar asigurările de la Aviației" is a link a landlord can send.
//
// This object is exported on its own rather than folded into the screen's schema because the
// calendar is a COMPONENT before it is a screen: 04 §8.11 gives the unified compliance calendar
// to SCR-FISC-002 once the fiscal lane exists (M4), and the route that mounts it there will
// spread this shape into its own `validateSearch` exactly as `features/insurance/search.ts`
// does here. See DEVIATIONS D-1132.
//
// NO PAGING. Every other list in this app pages; a calendar does not — a month is a window, and
// the window is what bounds it. The four lane queries each take 02 §7.4's ceiling of 200, which
// for a V1-sized portfolio is the whole set (D-1135 records what happens above it).
//
// `month` is READ leniently rather than validated strictly, and that is deliberate. TanStack
// Router parses search values with `JSON.parse` (D-1197), so a hand-typed `?month=2026` would
// arrive as the NUMBER 2026; a `z.string()` would then fail `validateSearch` and replace the
// whole screen with a Zod dump. `z.coerce.string()` accepts it, `isCalendarMonth` rejects it a
// moment later, and the calendar opens on the current month — which is what someone who typed a
// broken month wanted to see anyway.

import { z } from 'zod';

import { COMPLIANCE_SOURCES } from './sources';
import { isCalendarMonth, monthOfDate, type CalendarMonth } from './month';

/** The two renderings of one set of events (05 MAINT-009 — „month and agenda views"). */
export const CALENDAR_VIEWS = ['month', 'agenda'] as const;

export type CalendarView = (typeof CALENDAR_VIEWS)[number];

/** How far ahead the agenda looks when the URL does not say. A quarter — the planning horizon. */
export const DEFAULT_AGENDA_DAYS = 90;

/** The horizons the agenda offers. Any number in range is accepted from a typed URL. */
export const AGENDA_HORIZONS = [30, 90, 180, 365] as const;

export const complianceCalendarSearchSchema = z.object({
  /** Which rendering is showing. The month grid is the default — it is what „calendar" means. */
  calendar: z.enum(CALENDAR_VIEWS).default('month'),
  /** `YYYY-MM`. Absent, or unreadable, means the current month in Europe/Bucharest. */
  month: z.coerce.string().optional(),
  /** How many days ahead the agenda reaches. Ignored by the month grid, which has a window. */
  horizon: z.coerce.number().int().min(1).max(730).default(DEFAULT_AGENDA_DAYS),
  /** One lane, or every lane when absent. */
  source: z.enum(COMPLIANCE_SOURCES).optional(),
  /** One property's deadlines. Set from the filter, or by a deep link from SCR-PROP-003. */
  propertyId: z.string().trim().optional(),
});

export type ComplianceCalendarSearch = z.infer<typeof complianceCalendarSearchSchema>;

/**
 * The month the grid is on: the URL's when it is a readable `YYYY-MM`, otherwise today's.
 *
 * @param search the URL's state.
 * @param today `todayInBucharest()` — the zone every business date in this product is in, so a
 *   landlord in Madrid at 00:30 opens the Bucharest month.
 */
export function monthInView(search: ComplianceCalendarSearch, today: string): CalendarMonth {
  const asked = search.month;
  if (asked !== undefined && isCalendarMonth(asked)) return asked;
  return monthOfDate(today) ?? today.slice(0, 7);
}

/** Whether any lane or property filter is engaged — what the empty state needs to know. */
export function hasCalendarFilters(search: ComplianceCalendarSearch): boolean {
  return search.source !== undefined || search.propertyId !== undefined;
}

/**
 * Clearing the filters keeps the rendering and the month the user is looking at.
 *
 * The two cleared params are written as an explicit `undefined` rather than omitted, because a
 * host screen composes this result by SPREADING it over its own search object — omitting a key
 * there would leave the old value standing, which is the one bug a „șterge filtrele" button
 * must not have.
 */
export function clearedCalendarSearch(
  search: ComplianceCalendarSearch,
): ComplianceCalendarSearch {
  return {
    calendar: search.calendar,
    month: search.month,
    horizon: search.horizon,
    source: undefined,
    propertyId: undefined,
  };
}
